import { zodResolver } from "@hookform/resolvers/zod";
import { Trash } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { handleUpdateEvent } from "../../../API/Events/eventService";
import { handleCreateJob } from "../../../API/recruitment/recruitmentService";
import QuillEditor from "../../../lib/quill/quill";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import { AlertModal } from "../../modal/alert-modal";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import { useToast } from "../../ui/use-toast";
import {
  RecruitmentFormValues,
  recruitmentSchema,
  UpdateRecruitmentFormValues,
  updateRecruitmentSchema,
} from "./recruitment-zod-schema";

export const RecruitmentForm: React.ComponentType<any> = ({ initialData }) => {
  const router = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier l'offre d'emploi"
    : " Ajouter une offre d'emploi";
  const description = initialData ? "Modifier l'offre d'emploi" : " ";
  const toastMessage = initialData
    ? "Offre d'emploi modifié."
    : "Offre d'emploi ajoutée";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const defaultValues = initialData
    ? initialData
    : {
        title: "",
        imgUrl: [],
      };

  const form = useForm<RecruitmentFormValues | UpdateRecruitmentFormValues>({
    resolver: zodResolver(
      initialData ? updateRecruitmentSchema : recruitmentSchema
    ),
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateEvent(initialData, formData, router);
      } else {
        await handleCreateJob(
          formData,
          form.setValue,
          recruitmentSchema,
          router
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      setLoading(true);

      router(`/admin-dashboard/recrutement`);
    } catch (error: any) {
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const breadcrumbItems = [
    { title: "recrutement", link: "/admin-dashboard/recrutement" },
    { title: "Ajouter", link: "/admin-dashboard/evenements/ajouter" },
  ];

  return (
    <>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={onDelete}
        loading={loading}
        description="Événement supprimé avec succès"
      />

      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
            {initialData && (
              <Button
                disabled={loading}
                variant="destructive"
                size="sm"
                onClick={() => setOpen(true)}
              >
                <Trash className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Titre..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <QuillEditor
                        value={field.value || ""}
                        onChange={(value: string) => {
                          field.onChange(value);
                          form.trigger("description");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={form.control}
                name="imgUrl"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        onChange={(files) =>
                          form.setValue(
                            "imgUrl",
                            files.map((file) => ({
                              name: file.fileName,
                              key: file.id,
                              size: parseInt(file.fileSize),
                              fileName: file.fileName,
                              fileSize: parseInt(file.fileSize),
                            }))
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <Button
                disabled={loading}
                isLoading={loading}
                className="ml-auto"
                type="submit"
              >
                {action}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
